// @systeme: interface
// Préparer à rebours : on choisit un modèle (anniversaire, rentrée…), on donne un titre et
// une date (ou on part d'un élément existant), le serveur montre les étapes qu'il prévoit
// avec leurs dates, on valide. Aucune date n'est calculée ici (règle R2).
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ChevronLeft } from 'lucide-react';
import { Link as LienRouteur, useLocation, useNavigate } from 'react-router';
import type { ApercuRetroplanning, ModeleRetroplanning } from '@foyer/shared';
import { z } from 'zod';
import { Chargement } from '../composants/Chargement';
import { useMessages } from '../composants/Messages';
import { api } from '../lib/api';
import { formaterJourCourt, formaterJourLong } from '../lib/dates';
import { couleurs, tailles } from '../theme/theme';

/** Ce que « Préparer… » sur un élément transmet à cet écran (validé : l'état de navigation n'est pas sûr). */
const SchemaElementDeDepart = z.object({
  elementId: z.string(),
  titre: z.string(),
  jour: z.string().nullable(),
});
type ElementDeDepart = z.infer<typeof SchemaElementDeDepart>;

function elementDeDepart(etat: unknown): ElementDeDepart | undefined {
  const lu = SchemaElementDeDepart.safeParse(etat);
  return lu.success ? lu.data : undefined;
}

export function NouvellePreparation() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const depart = elementDeDepart(state);
  const { dire, direErreur } = useMessages();
  const [modeles, setModeles] = useState<ModeleRetroplanning[] | undefined>();
  const [modele, setModele] = useState<ModeleRetroplanning | 'libre' | undefined>();
  const [titre, setTitre] = useState(depart?.titre ?? '');
  const [jour, setJour] = useState(depart?.jour ?? '');
  // L'aperçu est gardé avec la demande qui l'a produit : un aperçu d'une autre date ne s'affiche pas.
  const [apercu, setApercu] = useState<{ cle: string; valeur: ApercuRetroplanning } | undefined>();
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    api.retroplannings.modeles().then(setModeles).catch(direErreur);
  }, [direErreur]);

  const modeleCle = modele === 'libre' ? 'libre' : modele?.cle;

  // Dès qu'un modèle et un jour sont choisis, le serveur dit où il poserait les étapes.
  const cleApercu = modeleCle && modeleCle !== 'libre' && jour !== '' ? `${modeleCle}|${jour}` : '';
  useEffect(() => {
    if (cleApercu === '' || !modeleCle) return;
    let annule = false;
    api.retroplannings
      .apercu({ modeleCle, jour })
      .then((valeur) => !annule && setApercu({ cle: cleApercu, valeur }))
      .catch(() => undefined);
    return () => {
      annule = true;
    };
  }, [cleApercu, modeleCle, jour]);
  const etapesPrevues = apercu?.cle === cleApercu ? apercu.valeur.etapes : [];

  function choisir(m: ModeleRetroplanning | 'libre') {
    setModele(m);
    if (!depart && titre === '' && m !== 'libre') setTitre(m.nom);
  }

  const pret = modeleCle !== undefined && titre.trim() !== '' && jour !== '';

  async function enregistrer() {
    if (!pret || !modeleCle) return;
    setEnCours(true);
    try {
      const p = await api.retroplannings.creer(
        depart
          ? { modeleCle, elementId: depart.elementId }
          : { modeleCle, titre: titre.trim(), jour },
      );
      dire(
        p.jalons.length === 0
          ? 'C’est noté. Ajoute les étapes quand tu veux.'
          : `C’est prévu : ${p.jalons.length} étapes, à leur date.`,
      );
      void navigate(`/a-venir/preparations/${p.id}`);
    } catch (e) {
      direErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  if (!modeles) return <Chargement />;

  return (
    <Box sx={{ px: `${tailles.marge}px`, pt: 3, pb: 4 }}>
      <Button
        component={LienRouteur}
        to="/a-venir/preparations"
        startIcon={<ChevronLeft size={18} />}
        color="inherit"
        sx={{ px: 0, mb: 1 }}
      >
        Ce que je prépare
      </Button>
      <Typography variant="h1" component="h1" sx={{ mb: 0.5 }}>
        {depart ? `Préparer « ${depart.titre} »` : 'Qu’est-ce qu’on prépare ?'}
      </Typography>
      {depart && depart.jour && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {formaterJourLong(depart.jour)}
        </Typography>
      )}

      <Stack spacing={1} sx={{ mt: 2, mb: 3 }}>
        {modeles.map((m) => (
          <Paper
            key={m.cle}
            variant="outlined"
            onClick={() => choisir(m)}
            role="button"
            tabIndex={0}
            aria-pressed={modele !== 'libre' && modele?.cle === m.cle}
            sx={{
              p: 1.5,
              cursor: 'pointer',
              bgcolor:
                modele !== 'libre' && modele?.cle === m.cle ? couleurs.saugeClair : couleurs.blanc,
              borderColor: modele !== 'libre' && modele?.cle === m.cle ? couleurs.sauge : 'divider',
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>{m.nom}</Typography>
            <Typography variant="body2" color="text.secondary">
              {m.description}
            </Typography>
          </Paper>
        ))}
        <Button
          variant={modele === 'libre' ? 'contained' : 'outlined'}
          color="primary"
          onClick={() => choisir('libre')}
        >
          Autre chose : je mets mes propres étapes
        </Button>
      </Stack>

      {modele !== undefined && (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            void enregistrer();
          }}
        >
          <Stack spacing={2}>
            {!depart && (
              <>
                <TextField
                  label="Quoi ?"
                  value={titre}
                  onChange={(e) => setTitre(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                />
                <TextField
                  type="date"
                  label="Quel jour ?"
                  value={jour}
                  onChange={(e) => setJour(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}

            {etapesPrevues.length > 0 && (
              <Box>
                <Typography variant="h3" component="h2" sx={{ mb: 1 }}>
                  Je prévois
                </Typography>
                <List disablePadding>
                  {etapesPrevues.map((e) => (
                    <ListItem key={`${e.titre}-${e.decalageJours}`} disableGutters sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={e.titre}
                        secondary={
                          e.ramenee
                            ? `${formaterJourCourt(e.jour)} · dès maintenant, la date approche`
                            : formaterJourCourt(e.jour)
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                <Typography variant="body2" color="text.secondary">
                  Chaque étape se coche, se reporte ou se supprime comme le reste.
                </Typography>
              </Box>
            )}

            <Button type="submit" variant="contained" size="large" disabled={!pret || enCours}>
              C’est parti
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
